import { motion } from 'framer-motion';
import { Home, Building2, LandPlot, LayoutGrid, Euro, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const FilterPanel = ({
  activeFilter,
  setActiveFilter,
  activeMarketingFilter,
  setActiveMarketingFilter,
  showInactive,
  setShowInactive,
  stats,
  isMobile = false,
}) => {
  const filters = [
    { id: 'all', label: 'Alle', icon: LayoutGrid, count: stats.total },
    { id: 'haus', label: 'Häuser', icon: Home, count: stats.houses, color: 'bg-[#0f9d94]' },
    { id: 'wohnung', label: 'Wohnungen', icon: Building2, count: stats.apartments, color: 'bg-[#8b1e1e]' },
    { id: 'grundstueck', label: 'Grundstücke', icon: LandPlot, count: stats.properties, color: 'bg-[#8b5a2b]' },
  ];
  const marketingFilters = [
    { id: 'all', label: 'Alle', icon: LayoutGrid, count: stats.total },
    { id: 'sale', label: 'Verkauf', icon: Euro, count: stats.sale_total, color: 'bg-emerald-600' },
    { id: 'rent', label: 'Vermietung', icon: KeyRound, count: stats.rent_total, color: 'bg-sky-600' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="absolute left-3 right-3 top-3 z-10 lg:left-auto lg:right-4 lg:top-4"
      data-testid="filter-panel"
    >
      <div className="filter-panel flex flex-col gap-2 p-2 pl-14 sm:p-2.5 sm:pl-2.5">
        <div className="flex justify-end gap-1 overflow-x-auto pb-1 no-scrollbar lg:flex-wrap lg:justify-end lg:overflow-visible lg:pb-0">
          {filters.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilter === filter.id;

            return (
              <Button
                key={filter.id}
                data-testid={`filter-${filter.id}`}
                variant={isActive ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveFilter(filter.id)}
                aria-label={filter.label}
                title={filter.label}
                className={`
                  relative h-auto shrink-0 rounded-md transition-all duration-200
                  ${isMobile ? 'px-2 py-2' : 'px-2.5 py-2 lg:px-3'}
                  ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-700 hover:bg-stone-100'}
                `}
              >
                <div className={`flex items-center ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
                  {filter.color && <span className={`h-2 w-2 rounded-full ${filter.color}`} />}
                  <Icon className="h-4 w-4" />
                  {!isMobile && <span className="font-medium text-xs sm:text-sm">{filter.label}</span>}
                  <span
                    className={`
                      rounded-full px-1.5 py-0.5 text-xs
                      ${isActive ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600'}
                    `}
                  >
                    {filter.count}
                  </span>
                </div>
              </Button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 overflow-x-auto pb-1 no-scrollbar lg:justify-end lg:overflow-visible lg:pb-0">
          <div className="flex shrink-0 items-center gap-1 rounded-md bg-white/60 p-1">
            {marketingFilters.map((filter) => {
              const Icon = filter.icon;
              const isActive = activeMarketingFilter === filter.id;

              return (
                <Button
                  key={filter.id}
                  data-testid={`marketing-filter-${filter.id}`}
                  variant={isActive ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveMarketingFilter(filter.id)}
                  aria-label={filter.label}
                  title={filter.label}
                  className={`
                    relative h-auto shrink-0 rounded-md px-2 py-1.5 transition-all duration-200 lg:px-2.5
                    ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-700 hover:bg-stone-100'}
                  `}
                >
                  <div className="flex items-center gap-1.5">
                    {filter.color && <span className={`h-2 w-2 rounded-full ${filter.color}`} />}
                    <Icon className="h-3.5 w-3.5" />
                    {!isMobile && <span className="font-medium text-xs sm:text-sm">{filter.label}</span>}
                    <span
                      className={`
                        rounded-full px-1.5 py-0.5 text-xs
                        ${isActive ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600'}
                      `}
                    >
                      {filter.count}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>

          <Button
            data-testid="toggle-inactive"
            variant={showInactive ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setShowInactive((current) => !current)}
            className={`
              relative h-auto shrink-0 rounded-md px-2.5 py-2 transition-all duration-200 lg:px-3
              ${showInactive ? 'bg-slate-700 text-white shadow-md' : 'text-slate-700 hover:bg-stone-100'}
            `}
          >
            <span className="text-xs sm:text-sm">{showInactive ? 'Aktiv + historisch' : 'Nur aktiv'}</span>
          </Button>

          {!isMobile && stats.historical_total > 0 && (
            <div className="flex shrink-0 items-center gap-2 rounded-md bg-white/70 px-3 py-2 text-xs text-slate-500">
              <span className="h-2 w-2 rounded-full bg-slate-400" />
              <span>{stats.historical_total} historisch</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FilterPanel;
